<template>
  <v-layout row wrap class="my-5 mx-0" align-start>
    <v-flex xs12 sm10 offset-sm1 md8 offset-md2>
      <v-card outlined>
        <v-card-title class="rlr-rating-title pa-6 pb-2">
          <div class="d-flex align-center justify-space-between flex-grow-1">
            <div class="d-flex align-center rlr-rating-heading">
              <v-avatar color="primary" size="52" class="mr-4">
                <v-icon dark large>mdi-chart-line</v-icon>
              </v-avatar>
              <div>
                <h1 class="text-h4 font-weight-bold mb-1">RLR Rating</h1>
                <div class="text-subtitle-1 font-weight-regular text--secondary hidden-xs-only">
                  Clasamentul de performanță al piloților RLR
                </div>
              </div>
            </div>
            <div class="d-flex align-center ml-2 ml-sm-4 flex-shrink-0">
              <rlr-rating-diagnostics
                v-if="isAdministrator"
                ref="ratingDiagnostics"
                class="mr-1 mr-sm-2"
              />
              <v-btn
                v-if="$vuetify.breakpoint.xsOnly"
                icon
                color="primary"
                aria-label="Cum funcționează RLR Rating?"
                @click="methodologyDialog = true"
              >
                <v-icon>mdi-information-outline</v-icon>
              </v-btn>
              <v-btn
                v-else
                outlined
                color="primary"
                @click="methodologyDialog = true"
              >
                <v-icon left>mdi-information-outline</v-icon>
                Cum funcționează?
              </v-btn>
            </div>
          </div>
        </v-card-title>

        <v-divider />

        <v-card-text>
          <v-skeleton-loader v-if="loading" type="table-heading, table-thead, table-tbody" />
          <v-alert v-else-if="error" type="error" outlined>{{ error }}</v-alert>
          <template v-else-if="groups.length">
            <v-text-field
              v-model="driverSearch"
              label="Caută un pilot"
              placeholder="Nume sau prescurtare"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              hide-details
              class="mb-4"
            />
            <v-tabs v-model="activeSimulator" show-arrows>
              <v-tab v-for="group in groups" :key="group.simulator._id">
                <span>{{ group.simulator.acronym || group.simulator.title }}</span>
                <v-chip
                  x-small
                  label
                  class="ml-2 simulator-count"
                  :aria-label="driverCountLabel(group.ratings.length)"
                >
                  {{ group.ratings.length }}
                </v-chip>
              </v-tab>
            </v-tabs>
            <v-tabs-items v-model="activeSimulator">
              <v-tab-item v-for="group in groups" :key="group.simulator._id">
                <v-row v-if="!normalizeSearch(driverSearch)" class="mt-3 hidden-xs-only" dense>
                  <v-col
                    v-for="item in group.ratings.slice(0, 3)"
                    :key="item.driver._id"
                    cols="12"
                    sm="4"
                  >
                    <v-card
                      :to="driverProfileRoute(item.driver._id, group.simulator._id)"
                      :class="['podium-card', `podium-card--${item.rank}`]"
                      outlined
                      height="100%"
                    >
                      <v-card-text class="text-center pa-4">
                        <v-avatar
                          :class="['mb-2', podiumMetalClass(item.rank)]"
                          size="48"
                        >
                          <v-icon>{{ rankIcon(item.rank) }}</v-icon>
                        </v-avatar>
                        <div class="text-overline font-weight-bold">Locul {{ item.rank }}</div>
                        <div class="text-h6 font-weight-bold text-truncate">
                          {{ driverName(item.driver) }}
                        </div>
                        <div class="text-h4 font-weight-bold primary--text mt-2">
                          {{ item.rating }}
                        </div>
                        <div class="text-caption text--secondary">RLR Rating</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <v-data-iterator
                  v-if="$vuetify.breakpoint.xsOnly"
                  :items="filteredRatings(group.ratings)"
                  :items-per-page="25"
                  :footer-props="tableFooterProps"
                  class="mt-4"
                >
                  <template v-slot:no-data>
                    <div class="py-8 text-center text--secondary">
                      <v-icon class="mb-2" color="grey">mdi-account-search-outline</v-icon>
                      <div>Nu am găsit niciun pilot pentru „{{ driverSearch }}”.</div>
                    </div>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-card
                      :to="driverProfileRoute(item.driver._id, group.simulator._id)"
                      outlined
                      class="mobile-driver-card mb-2"
                    >
                      <v-card-text class="d-flex align-center pa-3">
                        <v-avatar
                          v-if="item.rank <= 3"
                          :class="podiumColorClass(item.rank)"
                          size="42"
                          class="mr-3 flex-shrink-0"
                        >
                          <v-icon small>{{ rankIcon(item.rank) }}</v-icon>
                        </v-avatar>
                        <div v-else class="mobile-rank text-h6 font-weight-bold text--secondary mr-3">
                          #{{ item.rank }}
                        </div>

                        <div class="mobile-driver-details mr-2">
                          <div class="font-weight-bold text-body-1 text-truncate">
                            {{ driverName(item.driver) }}
                          </div>
                          <div class="text-caption text--secondary">
                            {{ ratedSessionsLabel(item.ratedSessions) }}
                          </div>
                          <rlr-provisional-chip
                            v-if="item.provisional"
                            :rated-sessions="item.ratedSessions"
                            class="mt-1"
                          />
                        </div>

                        <div class="text-right ml-auto flex-shrink-0">
                          <div class="text-h5 font-weight-bold primary--text">{{ item.rating }}</div>
                          <div class="text-caption text--secondary">RLR Rating</div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </template>
                </v-data-iterator>

                <v-data-table
                  v-else
                  :headers="headers"
                  :items="filteredRatings(group.ratings)"
                  :items-per-page="25"
                  :footer-props="tableFooterProps"
                  class="mt-4"
                  disable-sort
                  item-class="clickable-rating-row"
                  @click:row="openDriverProfile($event, group.simulator._id)"
                >
                  <template v-slot:no-data>
                    <div class="py-8 text--secondary">
                      <v-icon class="mb-2" color="grey">mdi-account-search-outline</v-icon>
                      <div>Nu am găsit niciun pilot pentru „{{ driverSearch }}”.</div>
                    </div>
                  </template>
                  <template v-slot:[`item.rank`]="{ item }">
                    <v-chip
                      v-if="item.rank <= 3"
                      small
                      label
                      :class="['font-weight-bold', podiumColorClass(item.rank)]"
                    >
                      <v-icon left small>{{ rankIcon(item.rank) }}</v-icon>
                      #{{ item.rank }}
                    </v-chip>
                    <span v-else class="font-weight-bold text--secondary">#{{ item.rank }}</span>
                  </template>
                  <template v-slot:[`item.driver`]="{ item }">
                    <router-link
                      :to="driverProfileRoute(item.driver._id, group.simulator._id)"
                      @click.native.stop
                    >
                      {{ driverName(item.driver) }}
                    </router-link>
                    <span v-if="item.driver.profile.shorthand" class="text--secondary ml-1">
                      ({{ item.driver.profile.shorthand }})
                    </span>
                  </template>
                  <template v-slot:[`item.rating`]="{ item }">
                    <span class="text-h6 font-weight-bold primary--text">{{ item.rating }}</span>
                    <rlr-provisional-chip
                      v-if="item.provisional"
                      :rated-sessions="item.ratedSessions"
                      class="ml-2"
                    />
                  </template>
                  <template v-slot:[`item.ratedSessions`]="{ item }">
                    {{ item.ratedSessions }}
                  </template>
                </v-data-table>
              </v-tab-item>
            </v-tabs-items>
          </template>
          <p v-else class="text--secondary mb-0">
            Azi nu. De maine, da.
          </p>
        </v-card-text>
      </v-card>
    </v-flex>

    <v-dialog v-model="methodologyDialog" max-width="680">
      <v-card>
        <v-card-title class="text-h5">
          <v-icon left color="primary">mdi-chart-line</v-icon>
          Cum funcționează RLR Rating?
        </v-card-title>
        <v-card-text class="text-body-1">
          <p>
            RLR Rating estimează performanța unui pilot în raport cu adversarii întâlniți.
            Un rezultat peste așteptări aduce puncte, iar unul sub așteptări scade ratingul.
          </p>

          <v-list class="transparent pa-0" dense>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-steering</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-wrap">Rating separat pentru fiecare simulator</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-flag-checkered</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-wrap">
                  Sunt incluse doar cursele oficiale din sezoanele și etapele marcate Ranked
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-car-multiple</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-wrap">
                  O clasă trebuie să aibă minimum 5 mașini eligibile
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-account-clock</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-wrap">
                  Primele 5 curse ale unui pilot sunt considerate provizorii
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-account-group</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-wrap">
                  În cursele de echipă, ratingul mașinii este media piloților, iar fiecare primește/pierde aceleași puncte de rating.
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-alert type="info" text class="mt-4 mb-0">
            Toți piloții pornesc de la 2.500. Valoarea absolută contează mai puțin decât
            diferența de rating față de ceilalți piloți.
            Simulatorul poate avea configurat și un bonus de participare fix sau dinamic,
            afișat separat în istoricul fiecărei curse.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="methodologyDialog = false">Am înțeles</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import axios from '@/plugins/axios'
import RlrProvisionalChip from '@/components/RlrProvisionalChip.vue'
import RlrRatingDiagnostics from '@/components/RlrRatingDiagnostics.vue'

export default {
  name: 'RlrRatings',
  components: {RlrProvisionalChip, RlrRatingDiagnostics},
  data: () => ({
    loading: true,
    error: '',
    groups: [],
    activeSimulator: 0,
    methodologyDialog: false,
    driverSearch: '',
    tableFooterProps: {
      'items-per-page-options': [25, 50, -1],
      'items-per-page-text': 'Piloți pe pagină',
      'items-per-page-all-text': 'Toți'
    },
    headers: [
      {text: 'Loc', value: 'rank'},
      {text: 'Pilot', value: 'driver'},
      {text: 'RLR Rating', value: 'rating'},
      {text: 'Curse clasate', value: 'ratedSessions'}
    ]
  }),
  computed: {
    ...mapGetters('auth', ['isLoggedIn']),
    ...mapGetters('user', ['userRoles']),
    isAdministrator() {
      return this.userRoles.some(role => role && role.name === 'Administrator')
    }
  },
  methods: {
    ...mapActions('user', ['getUserRoles']),
    normalizeSearch(value) {
      return (value || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
    },
    filteredRatings(ratings) {
      const search = this.normalizeSearch(this.driverSearch)
      if (!search) return ratings

      return ratings.filter(item => {
        const profile = item.driver && item.driver.profile || {}
        return this.normalizeSearch([
          profile.firstName,
          profile.lastName,
          profile.shorthand
        ].filter(Boolean).join(' ')).includes(search)
      })
    },
    rankIcon(rank) {
      return rank === 1 ? 'mdi-trophy' : 'mdi-medal'
    },
    podiumMetalClass(rank) {
      return {
        1: 'podium-metal--gold',
        2: 'podium-metal--silver',
        3: 'podium-metal--bronze'
      }[rank] || ''
    },
    podiumColorClass(rank) {
      return {
        1: 'podium-color--gold',
        2: 'podium-color--silver',
        3: 'podium-color--bronze'
      }[rank] || ''
    },
    driverCountLabel(count) {
      return `${count} ${count === 1 ? 'pilot clasat' : 'piloți clasați'}`
    },
    ratedSessionsLabel(count) {
      return `${count} ${count === 1 ? 'cursă clasată' : 'curse clasate'}`
    },
    simulatorOrder(simulator) {
      const key = (simulator.acronym || simulator.title || '').toLowerCase()
      if (key === 'ir' || key === 'iracing') return 0
      if (key === 'ac') return 1
      if (key === 'acc') return 2
      return 3
    },
    driverProfileRoute(driverId, simulatorId) {
      return {
        path: `/driver-profile/${driverId}`,
        query: {from: 'rlr-rating', simulator: simulatorId}
      }
    },
    openDriverProfile(item, simulatorId) {
      this.$router.push(this.driverProfileRoute(item.driver._id, simulatorId))
    },
    driverName(driver) {
      const profile = driver.profile || {}
      return [profile.firstName, profile.lastName].filter(Boolean).join(' ') || 'Pilot RLR'
    }
  },
  async mounted() {
    if (this.isLoggedIn && !this.userRoles.length) {
      await this.getUserRoles().catch(() => null)
    }
    try {
      const response = await axios.get('/api/rlr-ratings')
      this.groups = (response.data.data || []).slice().sort((first, second) => {
        const order = this.simulatorOrder(first.simulator) - this.simulatorOrder(second.simulator)
        return order || first.simulator.title.localeCompare(second.simulator.title)
      })
      const simulatorId = this.$route.query.simulator
      const simulatorIndex = this.groups.findIndex(group => group.simulator._id === simulatorId)
      const iracingIndex = this.groups.findIndex(group => this.simulatorOrder(group.simulator) === 0)
      this.activeSimulator = simulatorIndex >= 0 ? simulatorIndex : Math.max(iracingIndex, 0)
      if (this.isAdministrator && this.$route.query.diagnostics === '1') {
        this.$nextTick(() => {
          if (this.$refs.ratingDiagnostics) {
            this.$refs.ratingDiagnostics.open(simulatorId)
          }
        })
      }
    } catch (error) {
      this.error = 'Clasamentul RLR Rating nu a putut fi încărcat.'
    } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped>
.rlr-rating-title {
  word-break: normal;
}

.rlr-rating-heading {
  min-width: 0;
}

.podium-card {
  border-top-width: 4px !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.podium-card:hover {
  transform: translateY(-2px);
}

.podium-card--1 {
  border-top-color: #d4af37 !important;
}

.podium-card--2 {
  border-top-color: #c0c0c0 !important;
}

.podium-card--3 {
  border-top-color: #ceba9b !important;
}

.podium-metal--gold,
.podium-metal--silver,
.podium-metal--bronze {
  color: #292929 !important;
  border: 1px solid rgba(0, 0, 0, 0.28) !important;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.85),
    inset 0 -1px 0 rgba(0, 0, 0, 0.2),
    0 1px 2px rgba(0, 0, 0, 0.18);
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.45);
}

.podium-metal--gold {
  background: linear-gradient(135deg, #9b7209 0%, #f8e38a 24%, #d4af37 48%, #fff0a3 68%, #9b7209 100%) !important;
}

.podium-metal--silver {
  background: linear-gradient(135deg, #858a90 0%, #f4f6f7 24%, #c0c0c0 48%, #ffffff 68%, #858a90 100%) !important;
}

.podium-metal--bronze {
  background: linear-gradient(135deg, #88583a 0%, #efd5bb 24%, #ceba9b 48%, #fae7d3 68%, #88583a 100%) !important;
}

.podium-color--gold,
.podium-color--silver,
.podium-color--bronze {
  color: #292929 !important;
}

.podium-color--gold {
  background-color: #d4af37 !important;
}

.podium-color--silver {
  background-color: #c0c0c0 !important;
}

.podium-color--bronze {
  background-color: #ceba9b !important;
}

.simulator-count {
  pointer-events: none;
}

.mobile-driver-card {
  border-left: 3px solid var(--v-primary-base) !important;
}

.mobile-driver-details {
  min-width: 0;
}

.mobile-rank {
  min-width: 42px;
  text-align: center;
}

::v-deep .clickable-rating-row {
  cursor: pointer;
}

::v-deep .clickable-rating-row:hover {
  background-color: rgba(25, 118, 210, 0.06) !important;
}
</style>
